import { Menu } from '@base-ui/react/menu'
import type { PartProps } from '../../../lib/part-props'

/**
 * Props interface for MenuSubmenuRoot component.
 * Extends base-ui's Menu.SubmenuRoot.Props for submenu functionality.
 */
interface MenuSubmenuRootProps extends PartProps<Menu.SubmenuRoot.Props> {}

/**
 * MenuSubmenuRoot - Creates a nested submenu within a parent menu.
 *
 * This component wraps base-ui's Menu.SubmenuRoot to enable nested menu
 * functionality. It manages the state and behavior of a submenu that opens
 * when its trigger is interacted with.
 *
 * Features:
 * - Automatic positioning relative to parent menu
 * - Hover and click interactions
 * - Keyboard navigation support (arrow keys)
 * - Proper ARIA structure for accessibility
 *
 * A submenu requires:
 * - MenuSubmenuRoot (this component)
 * - MenuSubmenuTrigger (the item that opens the submenu)
 * - Menu.Portal, Menu.Positioner, Menu.Popup (nested menu structure)
 *
 * @example
 * ```tsx
 * <Menu.SubmenuRoot>
 *   <Menu.SubmenuTrigger>
 *     More Options
 *   </Menu.SubmenuTrigger>
 *   <Menu.Portal>
 *     <Menu.Positioner>
 *       <Menu.Popup>
 *         <Menu.Item>Submenu Item 1</Menu.Item>
 *         <Menu.Item>Submenu Item 2</Menu.Item>
 *       </Menu.Popup>
 *     </Menu.Positioner>
 *   </Menu.Portal>
 * </Menu.SubmenuRoot>
 * ```
 */
function MenuSubmenuRoot({ children, ...props }: MenuSubmenuRootProps) {
  return <Menu.SubmenuRoot {...props}>{children}</Menu.SubmenuRoot>
}

export { MenuSubmenuRoot }
